/**
 * /genies/:id/immersive-setup — Immersive Genie Setup (configurator direction).
 *
 * Reinvention of test mode: the LEFT half IS the real Live Agent stage (light
 * page, brand-tinted genie avatar, gradient Start/End pill, action row, brand /
 * white transcript bubbles + text input) so the owner tests their genie exactly
 * as customers see it — now wired to a REAL voice session via
 * useVoiceAgentConversation, identical to LiveAgentPage. The RIGHT half is a
 * scroll-through "Set up your genie" pop-out (SetupPopout) which owns its own
 * data + edits + save (self-saving contract).
 *
 * Reuses: VoiceAgentAvatar + VoiceSessionActionRow (live-agent primitives),
 * useVoiceAgentConversation (real ElevenLabs session), SetupRow / SectionHeader
 * atoms (via SetupPopout), useAgent(id) (identity + session). Co-located prefix:
 * Setup*.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Plus, Send, Sliders, Phone } from "lucide-react";
import { VoiceAgentAvatar } from "@/components/VoiceAgent";
import { VoiceSessionActionRow } from "@/pages/agent-interfaces/live-agent/components/VoiceSessionActionRow";
import { useAgent } from "@/hooks/agent/useAgents";
import { useVoiceAgentConversation } from "@/hooks/voice/useVoiceAgentConversation";
import { BulkUploadProvider } from "@/contexts";
import { getGenieBrandTheme, withAlpha } from "@/utils/branding/genieBrandTheme";
import { SetupPopout } from "./SetupPopout";

interface TranscriptMessage {
  message: string;
  isUser: boolean;
  timestamp: Date;
}

const DEFAULT_AGENT_VOICE_VOLUME = 0.85;

const ImmersiveSetupPage = () => {
  const { id = "" } = useParams();
  const { data: agent, isLoading, isError } = useAgent(id);

  // Real ElevenLabs session — mirrors LiveAgentPage's useVoiceAgentConversation call.
  const voiceAgent = useVoiceAgentConversation(agent?.elevenlabs_id ?? undefined, {
    welcomeMessage: agent?.welcome_message || "Tap to start",
    agentData: agent,
  });

  // Real transcript bubbles, populated from the service's onMessage callback.
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [inputValue, setInputValue] = useState("");

  // Split audio / pause controls — kept in sync with the underlying service.
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isAgentVoiceMuted, setIsAgentVoiceMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [mobileTab, setMobileTab] = useState<"live" | "setup">("live");
  const [chatOpen, setChatOpen] = useState(false);

  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasElevenLabsId = Boolean(agent?.elevenlabs_id);

  const genieName = agent?.name || "Reception Genie";
  const tagline = agent?.description || "Your front desk genie";
  const theme = getGenieBrandTheme(agent);
  const brandColor = theme.primary;

  // Subscribe to the real message stream — both genie and user lines arrive here.
  useEffect(() => {
    const service = voiceAgent.getService();
    if (!service) return;
    service.onMessage((message, isUser) => {
      setMessages((prev) => [...prev, { message, isUser, timestamp: new Date() }]);
    });
  }, [voiceAgent]);

  // Reset split-audio / pause UI when the session disconnects.
  useEffect(() => {
    if (!voiceAgent.connected) {
      setIsMicMuted(false);
      setIsAgentVoiceMuted(false);
      setIsPaused(false);
    }
  }, [voiceAgent.connected]);

  // Auto-scroll the transcript to the newest message.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleToggleSession = async () => {
    if (!hasElevenLabsId) return;
    if (!voiceAgent.connected && !voiceAgent.isConnecting) {
      setMessages([]);
    }
    await voiceAgent.toggleConversation();
  };

  const handleToggleMicMute = async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    const next = !isMicMuted;
    try {
      await service.setMicMuted(next);
      setIsMicMuted(next);
    } catch (err) {
      console.error("Error toggling microphone mute", err);
    }
  };

  const handleToggleAgentVoice = async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    try {
      await service.setVolume(isAgentVoiceMuted ? DEFAULT_AGENT_VOICE_VOLUME : 0);
      setIsAgentVoiceMuted((x) => !x);
    } catch (err) {
      console.error("Error toggling agent voice output", err);
    }
  };

  const handleTogglePause = async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    try {
      if (isPaused) {
        service.interruptCurrentSpeech();
        await service.setMicMuted(false);
        await service.setVolume(DEFAULT_AGENT_VOICE_VOLUME);
        setIsMicMuted(false);
        setIsAgentVoiceMuted(false);
        setIsPaused(false);
      } else {
        service.sendContextualUpdate("The user paused and will return shortly");
        await service.setMicMuted(true);
        await service.setVolume(0);
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
        setIsPaused(true);
      }
    } catch (err) {
      console.error("Error toggling pause", err);
    }
  };

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || !hasElevenLabsId) return;
    setInputValue("");
    const userMsg = { message: trimmed, isUser: true, timestamp: new Date() };
    if (!voiceAgent.connected && !voiceAgent.isConnecting) {
      setMessages([userMsg]);
      await voiceAgent.toggleConversation();
    } else {
      setMessages((prev) => [...prev, userMsg]);
    }
    // Service queues the message internally until the session reaches "connected".
    voiceAgent.sendUserMessage(trimmed);
  };

  const statusLabel = voiceAgent.getStatusMessage();

  return (
    <BulkUploadProvider>
    <div
      data-hg-block="voice-immersive-shell"
      className="flex flex-col md:flex-row w-full h-screen overflow-hidden bg-genie-background"
    >
      {/* ════════ MOBILE TAB BAR ════════ */}
      <div className="md:hidden flex-shrink-0 flex border-b border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => setMobileTab("live")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
            mobileTab === "live"
              ? "text-genie-primary border-b-2 border-genie-primary"
              : "text-gray-400"
          }`}
        >
          <Phone className="w-4 h-4" />
          Live
        </button>
        <button
          type="button"
          onClick={() => setMobileTab("setup")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
            mobileTab === "setup"
              ? "text-genie-primary border-b-2 border-genie-primary"
              : "text-gray-400"
          }`}
        >
          <Sliders className="w-4 h-4" />
          Set up
        </button>
      </div>

      {/* ════════ LEFT — live agent stage ════════ */}
      <div
        data-hg-block="live-agent-stage"
        className={`flex-1 min-w-0 h-full relative flex flex-col md:flex-row items-center justify-center gap-7 px-5 md:pl-7 md:pr-5 overflow-y-auto ${
          mobileTab === "setup" ? "hidden md:flex" : "flex"
        }`}
      >
        {/* Floating back to My Genie */}
        <button
          type="button"
          onClick={() => window.history.back()}
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 h-8 pl-2 pr-3 rounded-full bg-white border border-gray-200 shadow-sm text-[12.5px] font-medium text-[#64748B] hover:text-genie-primary transition-colors z-10"
        >
          <ChevronLeft className="w-4 h-4" />
          My Genie
        </button>

        {/* ─── Voice area ─── */}
        <div
          data-hg-block="voice-area"
          className="w-full max-w-[360px] flex flex-col items-center"
        >
          <p className="text-base font-medium text-[#666] mb-5">{statusLabel}</p>

          {/* Genie avatar — real live-agent orb, driven by the live session state */}
          <div data-hg-block="genie-avatar" className="flex items-center justify-center">
            <VoiceAgentAvatar
              brandColor={brandColor}
              customBranding={agent?.branding}
              onClick={() => void handleToggleSession()}
              connected={voiceAgent.connected}
              isConnecting={voiceAgent.isConnecting}
              speaking={voiceAgent.speaking}
              listening={voiceAgent.listening && !isPaused && !isAgentVoiceMuted}
              thinking={voiceAgent.thinking}
              processing={voiceAgent.processing}
              searching={voiceAgent.searching}
              retrieving={voiceAgent.retrieving}
              size={200}
            />
          </div>

          <h2 className="text-lg font-bold text-[#222B45] mt-4 -tracking-[0.2px]">{genieName}</h2>
          <p className="text-[12.5px] text-[#94A3B8] mt-0.5">{tagline}</p>

          {/* Start / End conversation pill */}
          <button
            type="button"
            onClick={() => void handleToggleSession()}
            disabled={!hasElevenLabsId || voiceAgent.isConnecting}
            style={{ background: theme.gradient, boxShadow: `0 12px 24px -8px ${withAlpha(theme.primary, "66")}` }}
            className="mt-5 h-[46px] px-6 rounded-2xl flex items-center justify-center text-[14.5px] font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {voiceAgent.connected
              ? "End conversation"
              : voiceAgent.isConnecting
                ? "Connecting…"
                : "Start conversation"}
          </button>

          {/* Inline note when this genie has no live connection yet */}
          {!hasElevenLabsId && (
            <p className="mt-3 max-w-[280px] text-center text-[12px] leading-relaxed text-[#7C8B9C]">
              This genie isn't connected for a live test yet. Finish setting it up to talk to it here.
            </p>
          )}

          {/* Action row — real live-agent control row, wired to the live session */}
          <div data-hg-block="call-controls" className="mt-2 w-full">
            <VoiceSessionActionRow
              brandColor={brandColor}
              isMobile={false}
              isTranscriptFeatureEnabled
              voiceConnected={voiceAgent.connected}
              isMicMuted={isMicMuted}
              onToggleMicMute={() => void handleToggleMicMute()}
              isAgentVoiceMuted={isAgentVoiceMuted}
              onToggleAgentVoice={() => void handleToggleAgentVoice()}
              isPaused={isPaused}
              onTogglePause={() => void handleTogglePause()}
              onOpenChat={() => { setChatOpen((v) => !v); setTimeout(() => inputRef.current?.focus(), 50); }}
              chatOpen={chatOpen}
              infoTitle={genieName}
              infoDescription={tagline}
            />
          </div>
        </div>

        {/* ─── Transcript + text input — desktop: side column; mobile: bottom bar ─── */}
        {chatOpen && (
          <>
            {/* Desktop side column */}
            <div className="hidden md:flex w-[360px] h-full flex-col justify-center">
              <div className="max-h-[78%] flex flex-col">
                <div
                  ref={transcriptRef}
                  data-hg-block="transcript"
                  className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2.5 pt-1 pb-3 px-0.5"
                >
                  {messages.map((m, i) => {
                    const isGenie = !m.isUser;
                    return (
                      <div key={i} className={`flex ${isGenie ? "justify-start" : "justify-end"}`}>
                        <div
                          style={isGenie ? { background: theme.gradient } : undefined}
                          className={`max-w-[85%] p-2.5 rounded-[10px] text-[13.5px] leading-relaxed ${
                            isGenie
                              ? "text-white shadow-md mr-6"
                              : "bg-white text-[#111827] border border-gray-200 shadow-sm ml-6"
                          }`}
                        >
                          {m.message}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{ background: theme.gradient, boxShadow: `0 12px 24px -10px ${withAlpha(theme.primary, "4d")}` }}
                  className="mt-2.5 p-[1.5px] rounded-full"
                >
                  <div className="h-[46px] rounded-full bg-white flex items-center gap-2 pl-1.5 pr-1.5">
                    <button type="button" onClick={() => toast("Attach an image or file")} style={{ backgroundColor: withAlpha(theme.primary, "14") }} className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0">
                      <Plus className="w-4 h-4" style={{ color: theme.primary }} />
                    </button>
                    <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSend(); } }} placeholder="Type a message…" className="flex-1 min-w-0 text-sm text-[#111827] placeholder:text-gray-400 bg-transparent outline-none" />
                    <button type="button" onClick={handleSend} style={{ background: theme.gradient, boxShadow: `0 6px 14px -4px ${withAlpha(theme.primary, "66")}` }} className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0">
                      <Send className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile bottom bar — absolute so it doesn't push the voice area up */}
            <div className="md:hidden absolute bottom-4 left-4 right-4 z-10">
              <div
                style={{ background: theme.gradient, boxShadow: `0 12px 24px -10px ${withAlpha(theme.primary, "4d")}` }}
                className="p-[1.5px] rounded-full"
              >
                <div className="h-[46px] rounded-full bg-white flex items-center gap-2 pl-1.5 pr-1.5">
                  <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSend(); } }} placeholder="Type a message…" className="flex-1 min-w-0 text-sm text-[#111827] placeholder:text-gray-400 bg-transparent outline-none" />
                  <button type="button" onClick={handleSend} style={{ background: theme.gradient, boxShadow: `0 6px 14px -4px ${withAlpha(theme.primary, "66")}` }} className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0">
                    <Send className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ════════ RIGHT — setup pop-out (self-saving) ════════ */}
      <div className={`flex-1 min-h-0 flex flex-col md:contents ${mobileTab === "live" ? "hidden md:block" : ""}`}>
        <SetupPopout agent={agent} isLoading={isLoading} />
      </div>

      {/* Inline error note (list/page failed to load) — toast not appropriate for a passive load */}
      {isError && !isLoading && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white border border-genie-secondary/30 shadow-sm text-xs font-medium text-genie-secondary z-20">
          We couldn't load this genie. The preview shows sample data.
        </div>
      )}
    </div>
    </BulkUploadProvider>
  );
};

export default ImmersiveSetupPage;
